// Action Types
import { produce } from 'immer'

export const SET_LOADING = 'omnichannelUsage/setLoading'
export const SET_DATA = 'omnichannelUsage/setData'
export const SET_ERROR = 'omnichannelUsage/setError'
export const RESET = 'omnichannelUsage/reset'

// Action Creators
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setData = (payload) => ({ type: SET_DATA, payload })
export const setError = (payload) => ({ type: SET_ERROR, payload })
export const reset = () => ({ type: RESET })

// State
export const initialState = {
  loading: false,
  error: null,
  contractLimit: null,
  // Bounds of the contract year being shown. `contractEnd` is exclusive — the
  // API counts `sent_at >= start AND sent_at < end` — so the last day actually
  // covered is the day before it. `formatContractWindow` handles that.
  contractStart: null,
  contractEnd: null,
  // Headline metric — totals over the current contract year. Drives the
  // progress bar and banner thresholds.
  headline: {
    total: 0,
    byChannel: [],
  },
  // Per-channel breakdown for the table. Covers the same contract year as
  // `headline`, so the two totals agree (QCER-1104).
  table: {
    total: 0,
    byChannel: [],
  },
}

const EMPTY_BREAKDOWN = { total: 0, byChannel: [] }

export default function omnichannelUsage(state = initialState, action) {
  if (action.type === RESET) {
    return initialState
  }
  return produce(state, (newState) => {
    switch (action.type) {
      case SET_LOADING:
        newState.loading = action.payload
        // Starting a fetch retires the previous failure. Without this the page
        // would still read as errored while the retry is in flight, and would
        // only stop showing the error if the retry happened to fail too.
        if (action.payload) {
          newState.error = null
        }
        break
      case SET_DATA: {
        // Defensive defaults: if the BE ever omits `headline` or `table`
        // (e.g. partial-body error, org with no contract data), the page
        // should fall back to the initial-state shape instead of throwing.
        const payload = action.payload ?? {}
        const headline = payload.headline ?? {}
        const table = payload.table ?? {}
        newState.contractLimit = payload.contract_limit ?? null
        newState.contractStart = payload.contract_start ?? null
        newState.contractEnd = payload.contract_end ?? null
        newState.headline = {
          total: headline.total ?? 0,
          byChannel: headline.by_channel ?? [],
        }
        newState.table = {
          total: table.total ?? 0,
          byChannel: table.by_channel ?? [],
        }
        newState.error = null
        break
      }
      case SET_ERROR:
        newState.error = action.payload
        newState.loading = false
        // Drop the counts. Leaving them meant a failed fetch kept the previous
        // org's (or window's) numbers on screen under the new label, which is
        // how a 422 read as real data (QCER-1104). The limit and window bounds
        // go too — they described the counts we just discarded.
        newState.contractLimit = null
        newState.contractStart = null
        newState.contractEnd = null
        newState.headline = { ...EMPTY_BREAKDOWN }
        newState.table = { ...EMPTY_BREAKDOWN }
        break
      default:
        break
    }
  })
}
